// Fachregeln der Buchhaltung, gemeinsam für Server und Oberfläche (Prototyp RST, RKETTE, ausStatus, vorsteuer, ustVon;
// Fachwissen neuratek/buchhaltung/ablauf.md). Reine Funktionen, damit sie ohne Datenbank prüfbar sind.

export const RST = {
  vorgemerkt: ['vorgemerkt', 'accent'], versendet: ['versendet', 'neutral'], ueberfaellig: ['überfällig', 'warn'], erinnert: ['erinnert', 'warn'],
  kritisch: ['kritisch', 'crit'], bezahlt: ['bezahlt', 'ok'], storniert: ['storniert', 'neutral'], entfaellt: ['entfällt', 'neutral']
} as const;
export type RStatus = keyof typeof RST;
export const RKETTE: RStatus[] = ['vorgemerkt', 'versendet', 'ueberfaellig', 'erinnert', 'kritisch', 'bezahlt'];
export const ROFFEN: RStatus[] = ['kritisch', 'ueberfaellig', 'erinnert', 'versendet'];

export const r2 = (n: number) => Math.round(n * 100) / 100;
export const eur2 = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
export const eur0 = (n: number) => Math.floor(n).toLocaleString('de-DE') + ' €';
export const brutto = (netto: number) => r2(netto * 1.19);
export const ustAuf = (netto: number) => r2(netto * 0.19);
export const plusTage = (iso: string, n: number) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const faelligAm = (versand: string | null, ziel: number) => versand ? plusTage(versand, ziel) : '';
export const monatVon = (iso: string | null | undefined) => (iso || '').slice(0, 7);
export const datumText = (iso: string | null | undefined) => iso ? iso.split('-').reverse().join('.') : '–';

/** Rechnungsnummer RE-<Kunden-ID>-NN, fortlaufend je Kunde */
export const rechnungsNr = (kid: string, bisher: number) => `RE-${kid}-${String(bisher + 1).padStart(2, '0')}`;

/** Monate je Rechnung im Retainer-Rhythmus */
export const RH = { monatlich: 1, quartalsweise: 3, 'jährlich': 12 } as const;
export type Rhythmus = keyof typeof RH;
export const proMonat = (betrag: number, rh: Rhythmus) => r2(betrag / RH[rh]);
/** Nächster Rechnungstag eines Retainers nach `iso` */
export function naechsterRetainerTag(iso: string, rh: Rhythmus, anker?: number): string {
  const d = new Date(iso + 'T12:00:00Z');
  const tag = anker ?? d.getUTCDate();
  d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + RH[rh]);
  const letzter = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(tag, letzter));
  return d.toISOString().slice(0, 10);
}

/** Leistungsart in den Einnahmen (Prototyp LART) und Beschreibung auf der Rechnung (BESCHR) */
export const leistungsArt = (l: string) => /analyse/i.test(l) ? 'Unternehmensanalyse' : /Website/.test(l) ? 'Website' : /Konzept/.test(l) ? 'Beratung' : 'Sonstiges';

/* ---------- Ausgaben ---------- */

export const KOSTENSTELLEN_STANDARD = ['Software & Hosting', 'Vertrieb', 'Leistungserstellung', 'Förderungskosten', 'Verwaltung'];
export const STEUERFAELLE = ['19 %', '7 %', '0 %', 'steuerfrei', 'Reverse Charge', 'nicht steuerbar', 'aus Rechnung'] as const;
export const SITZE = ['Inland', 'EU', 'Drittland'] as const;
/** Zahlungsarten; „Privat…“ = aus Leons privatem Geld bezahlt, steht nie im Geschäftskonto (Zahlung gilt mit dem Eintrag) */
export const ZAHLUNGSARTEN = ['Mastercard', 'Überweisung', 'Lastschrift', 'PayPal', 'bar', 'Private Mastercard', 'Privates Paypal', 'Private Überweisung'] as const;

export interface Lieferant { name: string; sitz: string; st: string; kst: string; fv: string; std: number | null; alias: string; hinweis: string; verzicht_bis?: number | null }
export interface AusgabeKern {
  lief: string | null; kontotext: string | null; beschr: string; ein: string | null; rdat: string | null; zdat: string | null; brutto: number | null;
  rechnung: string | null; verzicht: boolean; st: string | null; kst: string | null; fv: string | null;
}
const LV = (L: Record<string, Lieferant>, n: string | null): Lieferant => (n && L[n]) || { name: n ?? '', sitz: 'Inland', st: '', kst: 'Vertrieb', fv: 'variabel', std: null, alias: '', hinweis: '' };
/** Steuerfall erst mit Beleg (oder bewusstem Verzicht) */
export const steuerfall = (a: AusgabeKern, L: Record<string, Lieferant>) => a.rechnung || a.verzicht ? (a.st || LV(L, a.lief).st) : '';
export const kostenstelle = (a: AusgabeKern, L: Record<string, Lieferant>) => a.kst || (a.lief && LV(L, a.lief).kst !== 'nachfragen' ? LV(L, a.lief).kst : '');
export const fixVariabel = (a: AusgabeKern, L: Record<string, Lieferant>) => a.fv || (a.lief && LV(L, a.lief).fv !== 'nachfragen' ? LV(L, a.lief).fv : '');
export function ausgabeFehlt(a: AusgabeKern, L: Record<string, Lieferant>): string[] {
  const f: string[] = [];
  if (!a.rechnung && !a.verzicht) f.push('Rechnung');
  if (!a.zdat) f.push('Zahlung');
  if (!steuerfall(a, L) && (a.rechnung || a.verzicht)) f.push('Steuerfall');
  // Sitz nur bei Reverse Charge (EU 46/47, Drittland 84/85); bei 19 % egal (Leon 2026-11-03)
  if (steuerfall(a, L) === 'Reverse Charge' && a.lief && !LV(L, a.lief).sitz) f.push('Sitz');
  if (!kostenstelle(a, L)) f.push('Kostenstelle');
  if (!fixVariabel(a, L)) f.push('Fix/variabel');
  if (!a.beschr) f.push('Beschreibung');
  if (a.rechnung && !a.rdat) f.push('Rechnungsdatum');
  return f;
}
export function ausgabeStatus(a: AusgabeKern, L: Record<string, Lieferant>): [string, 'warn' | 'ok'] {
  const f = ausgabeFehlt(a, L);
  return f.length ? ['warte auf ' + f.join(', '), 'warn'] : a.verzicht ? ['ohne Beleg', 'ok'] : ['vollständig', 'ok'];
}
/** Monat einer Ausgabe: Rechnungsdatum, sonst Zahlung, sonst erwartet */
export const ausgabeMonat = (a: AusgabeKern) => monatVon(a.rdat || a.zdat || a.ein);
/** Vorsteuer erst bei Beleg, Zahlung und Steuerfall; null = noch nicht bestätigt */
export function vorsteuer(a: AusgabeKern, L: Record<string, Lieferant>): number | null {
  const s = steuerfall(a, L);
  if (!a.zdat || !a.rechnung || a.brutto === null) return null;
  return s === '19 %' ? r2(-a.brutto * 19 / 119) : s === '7 %' ? r2(-a.brutto * 7 / 107) : 0;
}
export const nettoAufwand = (a: AusgabeKern, L: Record<string, Lieferant>) => a.zdat && a.brutto !== null ? r2(-a.brutto - (vorsteuer(a, L) || 0)) : null;
export const teraVorsteuer = (x: { beleg: string | null; st: string; betrag: number }) => x.beleg && x.st ? r2(x.betrag * (x.st === '19 %' ? 19 / 119 : 7 / 107)) : 0;

/* ---------- Umsatzsteuer ---------- */

export interface UstEingabe {
  /** Netto der Rechnungen und Einnahmen mit Rechnungsdatum im Monat (ohne vorgemerkte, stornierte, entfallene) */
  umsaetze: (number | { was: string; netto: number })[];
  /** Einnahmen ohne Umsatzsteuer: nur in der Übersicht „Geld bekommen“, nicht in der Voranmeldung */
  ohneUst?: { was: string; netto: number }[];
  ausgaben: (AusgabeKern & { name: string })[];
  tera: { was: string; betrag: number; beleg: string | null; st: string }[];
  /** vorgemerkte Rechnungen mit Versand im Monat: zählen erst mit dem Versand */
  vorgemerkt: { firma: string; versand: string }[];
  lieferanten: Record<string, Lieferant>;
  tagText: (iso: string) => string;
}
/** Voranmeldung je Monat (Prototyp ustVon): Bemessungsgrundlagen in vollen Euro, Steuern mit Cent; EU 46/47 getrennt von Drittland 84/85 */
export function umsatzsteuer(e: UstEingabe) {
  const offen: string[] = [];
  const U = e.umsaetze.map(x => typeof x === 'number' ? { was: 'Umsatz', netto: x } : x);
  const ums = U.reduce((a, b) => a + b.netto, 0);
  // Übersicht für Leon (2026-11-03): Geld bekommen, Geld ausgegeben, davon ohne zurückholbare Umsatzsteuer; je Summe die Einzelbeträge
  const bekommen = [...U.map(x => ({ was: x.was, netto: r2(x.netto), ust: r2(x.netto * .19), brutto: r2(x.netto * 1.19) })), ...(e.ohneUst ?? []).map(x => ({ was: `${x.was}, ohne Umsatzsteuer`, netto: r2(x.netto), ust: 0, brutto: r2(x.netto) }))];
  const ausgegeben: { was: string; brutto: number; vst: number; art: string }[] = [];
  let eu = 0, dl = 0, vst = 0;
  for (const a of e.ausgaben) {
    const s = steuerfall(a, e.lieferanten), [stat] = ausgabeStatus(a, e.lieferanten);
    if (stat.startsWith('warte')) { offen.push(`${a.name}: ${stat}; zählt erst, wenn es geklärt ist`); continue; }
    ausgegeben.push({ was: a.name, brutto: r2(-(a.brutto ?? 0)), vst: vorsteuer(a, e.lieferanten) || 0, art: a.verzicht && !a.rechnung ? 'ohne Beleg' : s === 'Reverse Charge' ? 'Reverse Charge (keine Umsatzsteuer im Preis)' : s || 'ohne Steuerfall' });
    if (s === 'Reverse Charge') {
      const sitz = LV(e.lieferanten, a.lief).sitz;
      if (sitz === 'EU') eu += -(a.brutto ?? 0); else if (sitz === 'Drittland') dl += -(a.brutto ?? 0); else offen.push(`${a.name}: Sitz des Lieferanten fehlt (46/47 oder 84/85)`);
    } else vst += vorsteuer(a, e.lieferanten) || 0;
  }
  for (const x of e.tera) {
    if (x.st && x.beleg) vst += teraVorsteuer(x); else if (x.st) offen.push(`Tera: ${x.was} ohne Beleg, keine Vorsteuer`);
    ausgegeben.push({ was: `Tera: ${x.was}`, brutto: r2(x.betrag), vst: teraVorsteuer(x), art: x.st && x.beleg ? x.st : x.beleg ? 'ohne Steuerfall' : 'ohne Beleg' });
  }
  const summe3 = (L: { brutto: number }[]) => r2(L.reduce((a, b) => a + b.brutto, 0));
  const ohne = ausgegeben.filter(x => !x.vst);
  const uebersicht = { bekommen: { summe: summe3(bekommen), netto: r2(ums), L: bekommen }, ausgegeben: { summe: summe3(ausgegeben), vst: r2(ausgegeben.reduce((a, b) => a + b.vst, 0)), L: ausgegeben }, ohne: { summe: summe3(ohne), L: ohne } };
  for (const r of e.vorgemerkt) offen.push(`Rechnung an ${r.firma} geht erst am ${e.tagText(r.versand)} raus und zählt dann in diesem Monat`);
  const s81 = r2(Math.floor(ums) * .19), s47 = r2(Math.floor(eu) * .19), s85 = r2(Math.floor(dl) * .19), v66 = r2(vst), s67 = r2(s47 + s85);
  return { ums: r2(ums), eu: r2(eu), dl: r2(dl), s81, s47, s85, v66, s67, z: r2(s81 + s47 + s85 - v66 - s67), offen, uebersicht };
}

/* ---------- BAFA ---------- */

export { BAFA_ST } from './bafa.js';

/* ---------- Einstellungen und Vorlagen ---------- */

export const BH_STANDARD = { ziel: 14, versandNach: 14, nachStunde: 7, vorschau: true, nachfrist: 7, uploadTag: 'Freitag', ustTag: 1, abgabe: 10, vonUhr: 7, bisUhr: 18,
  // BAFA (Leon 2026-10-29): Erinnerung nach 10, Aufgabe „Kunde anrufen“ nach 20 Tagen; Unterlagen nach 7 Tagen; Auszahlung ab Woche 6 alle 3 Wochen
  bafaErinnern: 10, bafaKritisch: 20, bafaUnterlagen: 7, bafaAuszahlungAb: 42, bafaAuszahlungTakt: 21, vertragErinnern: 7 };
export type BhCfg = typeof BH_STANDARD;
export const BH_GRENZEN: Record<string, [number, number]> = { ziel: [1, 120], versandNach: [0, 90], nachStunde: [0, 90], nachfrist: [1, 60], ustTag: [1, 9], abgabe: [1, 31], vonUhr: [0, 23], bisUhr: [1, 24],
  bafaErinnern: [1, 60], bafaKritisch: [2, 90], bafaUnterlagen: [1, 60], bafaAuszahlungAb: [7, 180], bafaAuszahlungTakt: [7, 90], vertragErinnern: [1, 60] };
export const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

/** Absendername von buchhaltung@ für Kunden (Deckname, Leon 2026-10-30) */
export const BH_NAME = 'Markus Springer';
/**
 * Anrede der BAFA-Mails: „Sehr geehrte Frau Muster,“ aus Anrede und Nachname (aus der Förderanfrage); ohne Anrede mit dem Namen des
 * Ansprechpartners „Guten Tag Max Muster,“ (das CRM kennt keine Anrede); ohne beides „Sehr geehrte Damen und Herren,“.
 */
export const sehrGeehrte = (anrede: string | null | undefined, nachname: string | null | undefined, name?: string | null) => {
  const n = String(nachname ?? '').trim(), a = String(anrede ?? '').trim(), v = String(name ?? '').trim();
  return n && /frau/i.test(a) ? `Sehr geehrte Frau ${n},` : n && /herr/i.test(a) ? `Sehr geehrter Herr ${n},` : v ? `Guten Tag ${v},` : 'Sehr geehrte Damen und Herren,';
};
export const RTEXT_STANDARD: [string, string, string][] = [['Unternehmensanalyse', 'Strategische Unternehmensanalyse', 'aus dem Bestand'], ['Website', 'Website-Konzeption & Umsetzung', 'aus dem Bestand'], ['Konzeptentwicklung', 'Konzeptentwicklung – {Bereich}', 'Leon 2026-10-27']];
/** Stichpunkte je Leistung auf der Rechnung ({{Punkt}}), immer gleich (Leon 2026-10-27) */
export const RPUNKTE: Record<string, string[]> = {
  Konzeptentwicklung: [
    'Anforderungsanalyse und Erstellung eines User Requirements Document (URD) für die geplante Software- oder KI-Lösung',
    'Technische und wirtschaftliche Machbarkeitsanalyse inklusive Bewertung von Systemarchitektur, Technologien und Schnittstellen',
    'Ausarbeitung eines entwicklungsreifen Gesamtkonzepts mit Kostenabschätzung und unverbindlichem Umsetzungsangebot'
  ]
};
/** Titel auf der Rechnung: {Bereich} aus dem Vertrag; ohne Bereich fällt der Zusatz weg */
export const rechnungsTitel = (titel: string, bereich: string) => bereich.trim() ? titel.replace('{Bereich}', bereich.trim()) : titel.replace(/\s*[–-]\s*\{Bereich\}/, '').replace('{Bereich}', '');
export const ANGABEN = [['Absender', 'Firmenname und Inhaber, wie auf der Rechnung'], ['Anschrift', ''], ['Steuernummer oder USt-IdNr.', ''], ['Bankverbindung', 'Kontoinhaber, IBAN, BIC'], ['Unterzeichner', 'erscheint unter dem Text']] as const;

/** Mailvorlage füllen; {Anrede} aus „Herr Muster“ wird „Sehr geehrter Herr Muster,“, ohne Geschlecht „Guten Tag <Name>,“, ohne Namen „Sehr geehrte Damen und Herren,“ */
const anredeSatz = (an: string) => { const n = an.trim(); return /^herr /i.test(n) ? `Sehr geehrter ${n},` : /^frau /i.test(n) ? `Sehr geehrte ${n},` : n && !/damen und herren/i.test(n) ? `Guten Tag ${n},` : 'Sehr geehrte Damen und Herren,'; };
export function bhVorlage(t: string, w: Record<string, string>): string {
  return t.replace(/\{(Anrede|Rechnungsnummer|Betrag|Leistung|Rechnungsdatum|Fällig am|Neue Frist)\}/g, (_, k: string) => k === 'Anrede' ? anredeSatz(w.Anrede ?? '') : w[k] ?? '');
}
/** Leistung ohne Klammerzusatz (Retainer, monatlich) für Betreff und Mailtext */
export const leistungKurz = (l: string) => l.replace(/ \(.*\)$/, '');
